import { Action } from 'redux';

import trans from '../../helpers/trans';
import { Counselor, CounselorFormData } from '../../models/Counselor/Counselor';
import { getCounselorAllCandidatesApiCall, patchCounselorApiCall, postCounselorApiCall } from '../../models/Counselor/CounselorService';
import { transformToAddCounselorRequest, transformToArchiveCounselorRequest, transformToEditCounselorRequest } from '../../models/Counselor/CounselorTransformers';
import { isFetchResultSuccessful } from '../../models/FetchResult';
import { TypedDispatch } from '../store';
import {
    setCounselor,
    setError,
    setHasActiveCandidates,
    setIsLoading,
    setIsSuccessful,
    setIsSuccessfullyArchived,
    setIsUpdating,
} from './counselor';

export const createCounselor = (formData: CounselorFormData, organisationId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsUpdating(true));
    dispatch(setIsSuccessful(false));
    dispatch(setError(''));

    try {
        const addCounselorRequest = transformToAddCounselorRequest(formData, organisationId);

        const counselorResponse = await postCounselorApiCall(addCounselorRequest);

        if (!isFetchResultSuccessful(counselorResponse)) {
            dispatch(setError(trans('errors.unknownError')));
            return;
        }

        dispatch(setCounselor(counselorResponse.data));
        dispatch(setIsSuccessful(true));
    } catch (error) {
        console.error('[createCounselor]', error);
    } finally {
        dispatch(setIsUpdating(false));
    }
};

export const updateCounselor = (formData: CounselorFormData, organisationId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsUpdating(true));
    dispatch(setIsSuccessful(false));
    dispatch(setError(''));

    try {
        const editCounselorRequest = transformToEditCounselorRequest(formData, organisationId);

        const counselorResponse = await patchCounselorApiCall(editCounselorRequest);

        if (!isFetchResultSuccessful(counselorResponse)) {
            dispatch(setError(trans('errors.unknownError')));
            return;
        }

        dispatch(setCounselor(counselorResponse.data));
        dispatch(setIsSuccessful(true));
    } catch (error) {
        console.error('[updateCounselor]', error);
    } finally {
        dispatch(setIsUpdating(false));
    }
};

export const finishCreateCounselor = () => (dispatch: TypedDispatch): Action => (
    dispatch(setIsSuccessful(false))
);

export const fetchCounselorCandidates = (organisationUuid: string, counselorUuid: string) => async (dispatch: TypedDispatch) => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const candidatesResponse = await getCounselorAllCandidatesApiCall(organisationUuid, counselorUuid);

        if (!isFetchResultSuccessful(candidatesResponse)) {
            dispatch(setError(trans('errors.unknownError')));
            return;
        }

        const allCandidates = candidatesResponse.data;
        const hasActiveCandidates = allCandidates.length > 0;

        dispatch(setHasActiveCandidates(hasActiveCandidates));
    } catch (error) {
        console.error('[fetchCounselorCandidates]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const archiveCounselor = (counselor: Counselor, organisationUuid: string) => async (dispatch: TypedDispatch) => {
    dispatch(setIsLoading(true));
    dispatch(setIsSuccessfullyArchived(false));
    dispatch(setError(''));

    try {
        const archiveCounselorRequest = transformToArchiveCounselorRequest(counselor, organisationUuid);
        const archiveCounselorResponse = await patchCounselorApiCall(archiveCounselorRequest);

        if (!isFetchResultSuccessful(archiveCounselorResponse)) {
            dispatch(setError(archiveCounselorResponse.error));
            return;
        }

        dispatch(setIsSuccessfullyArchived(true));
    } catch (error) {
        console.error('[archiveCounselor]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};
